import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../utils/api';
import {
  buildEightDayChartModel,
  pickEntityType,
} from '../huntForecastUtils';

/**
 * Field-edition 8-day scores bar chart for the current top pick.
 */
export default function ForecastEightDayChart({ pick }) {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!pick?.id) {
      setDays([]);
      setError('');
      return undefined;
    }
    let cancelled = false;
    const entityType = pickEntityType(pick.kind);
    const basePath = entityType === 'geo-feature' ? '/features' : '/hunt-locations';

    setLoading(true);
    setError('');
    api.get(`${basePath}/${pick.id}/optimal_days/?days=8`)
      .then((resp) => {
        if (cancelled) return;
        setDays(Array.isArray(resp?.data?.days) ? resp.data.days : []);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setDays([]);
        setError('Unable to load 8-day outlook.');
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [pick?.id, pick?.kind]);

  const model = useMemo(() => buildEightDayChartModel(days), [days]);

  return (
    <section className="hf-eight-day" data-testid="hf-eight-day" aria-label="8-day scores">
      <div className="hf-eight-day__head">
        <span className="hf-eight-day__title">8-Day Scores</span>
        {model.bestCaption ? (
          <span className="hf-eight-day__best" data-testid="hf-eight-day-best">
            {model.bestCaption}
          </span>
        ) : null}
      </div>

      {loading && model.days.length === 0 ? (
        <p className="hf-eight-day__status">Loading outlook…</p>
      ) : error && model.days.length === 0 ? (
        <p className="hf-eight-day__status">{error}</p>
      ) : model.days.length === 0 ? (
        <p className="hf-eight-day__status">No outlook data available.</p>
      ) : (
        <>
          <div className="hf-eight-day__bars" role="img" aria-label="Daily hunt scores">
            {model.days.map((d) => (
              <div key={d.date || `${d.dow}-${d.day}`} className="hf-eight-day__col">
                <span className="hf-eight-day__score">
                  {d.score == null ? '—' : d.score}
                </span>
                <div
                  className={`hf-eight-day__bar ${d.bandClass}`}
                  style={{ height: `${Math.max(8, d.height)}%` }}
                  title={d.score == null ? undefined : `Score ${d.score}`}
                />
              </div>
            ))}
          </div>
          <div className="hf-eight-day__labels">
            {model.days.map((d) => (
              <span
                key={`lab-${d.date || `${d.dow}-${d.day}`}`}
                className={`hf-eight-day__label${d.isBest ? ' is-best' : ''}`}
              >
                {d.dow}
                <br />
                {d.day}
              </span>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
